import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { useApiService } from '../../services/api';
import './Payment1.css';

const Payment = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === 'rtl';
  const currentLanguage = i18n.language;
  const navigate = useNavigate();
  const location = useLocation();
  const apiService = useApiService();
  const [quoteId, setQuoteId] = useState('');
  
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    notes: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});

  // Set document direction based on language
  useEffect(() => {
    document.documentElement.setAttribute('dir', isRTL ? 'rtl' : 'ltr');
    document.documentElement.setAttribute('lang', currentLanguage);
  }, [isRTL, currentLanguage]);

  // Get quote ID from location state
  useEffect(() => {
    if (location.state && location.state.quoteId) {
      setQuoteId(location.state.quoteId);
    } else {
      setError(t('payment.no-quote-error') || 'لم يتم العثور على بيانات الطلب. يرجى العودة وإعادة المحاولة.');
    }
  }, [location.state, t]);

  // Validation functions
  const validateName = (name) => {
    // Only letters and spaces allowed, minimum 2 characters
    const nameRegex = /^[a-zA-Zأ-ي\s]{2,}$/;
    if (!name.trim()) {
      return t('validation.name.required') || 'الاسم مطلوب';
    }
    if (!nameRegex.test(name.trim())) {
      return t('validation.name.invalid') || 'الاسم يجب أن يحتوي على أحرف فقط (على الأقل حرفين)';
    }
    return '';
  };

  const validatePhone = (phone) => {
    // Oman phone number validation
    // Mobile: 9XXXXXXX (8 digits starting with 9)
    // Landline: 2XXXXXXX (8 digits starting with 2)
    const omanPhoneRegex = /^[92]\d{7}$/;
    
    if (!phone.trim()) {
      return t('validation.phone.required') || 'رقم الهاتف مطلوب';
    }
    
    // Remove any spaces or special characters
    const cleanPhone = phone.replace(/\D/g, '');
    
    if (!omanPhoneRegex.test(cleanPhone)) {
      return t('validation.phone.invalid') || 'رقم الهاتف يجب أن يكون 8 أرقام ويبدأ بـ 9 (موبايل) أو 2 (هاتف ثابت)';
    }
    
    return '';
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email.trim()) {
      return t('validation.email.required') || 'البريد الإلكتروني مطلوب';
    }
    if (!emailRegex.test(email.trim())) {
      return t('validation.email.invalid') || 'البريد الإلكتروني غير صحيح';
    }
    return '';
  };

  // Handle input change with validation
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // For phone field, only allow numbers and limit to 8 digits
    if (name === 'phone') {
      const numbersOnly = value.replace(/\D/g, '');
      // Limit to 8 digits maximum
      const limitedNumbers = numbersOnly.slice(0, 8);
      setFormData({
        ...formData,
        [name]: limitedNumbers
      });
    }
    // For name field, only allow letters and spaces
    else if (name === 'name') {
      const lettersOnly = value.replace(/[^a-zA-Zأ-ي\s]/g, '');
      setFormData({
        ...formData,
        [name]: lettersOnly
      });
    }
    else {
      setFormData({
        ...formData,
        [name]: value
      });
    }

    // Clear field error when user starts typing
    if (fieldErrors[name]) {
      setFieldErrors({
        ...fieldErrors,
        [name]: ''
      });
    }
  };

  // Validate single field on blur
  const handleFieldBlur = (fieldName) => {
    let error = '';
    const value = formData[fieldName];

    switch (fieldName) {
      case 'name':
        error = validateName(value);
        break;
      case 'phone':
        error = validatePhone(value);
        break;
      case 'email':
        error = validateEmail(value);
        break;
      default:
        break;
    }

    setFieldErrors({
      ...fieldErrors,
      [fieldName]: error
    });
  };

  // Validate all fields
  const validateForm = () => {
    const errors = {
      name: validateName(formData.name),
      phone: validatePhone(formData.phone),
      email: validateEmail(formData.email)
    };

    setFieldErrors(errors);
    
    // Return true if no errors
    return !Object.values(errors).some(error => error !== '');
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Clear any previous general errors
    setError('');
    
    // Validate form
    if (!validateForm()) {
      setError(t('payment.validation-error') || 'يرجى تصحيح الأخطاء في النموذج');
      return;
    }
    
    // Check if we have a quote ID
    if (!quoteId) {
      setError(t('payment.no-quote-id') || 'لم يتم العثور على معرف الطلب. يرجى العودة وإعادة المحاولة.');
      return;
    }
    
    // Show loading state
    setIsLoading(true);
    
    try {
      // Prepare request data based on API requirements
      const orderData = {
        quote_id: quoteId,
        user_email: formData.email.trim(),
        user_name: formData.name.trim(),
        phone_number: formData.phone.trim(),
        customer_notes: formData.notes.trim(),
      };
      
      const response = await apiService.translations.placeOrder(orderData);

      // If response contains payment_link, redirect to it
      if (response.data && response.data.payment_link) {
        window.location.href = response.data.payment_link;
      } else {
        // Fallback to success page if no payment link
        navigate('/payment-success', { 
          state: { 
            orderDetails: {
              orderId: response.data.id,
              date: response.data.created_at,
              status: response.data.status,
              amount: `${response.data.total_price} ${response.data.currency}`,
              message: response.data.message
            }
          } 
        });
      }
    } catch (err) {
      setError(t('payment.api-error') || 'حدث خطأ أثناء معالجة الطلب. يرجى المحاولة مرة أخرى.');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle back button
  const handleBack = () => {
    navigate(-1);
  };

  // Show error state if no quote ID
  if (error && (error.includes('لم يتم العثور على بيانات الطلب') || error.includes('no-quote-error'))) {
    return (
      <div className="payment-page">
        <div className="payment-container">
          <div className="payment-form-wrapper">
            <div className="error-message">{error}</div>
            <button 
              className="back-btn" 
              onClick={handleBack}
            >
              {t('payment.back') || 'العودة'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="payment-page">
      <div className="payment-container">
        <h1 className="payment-title">
          {t('payment.title') || 'عملية الدفع'}
        </h1>
        <div className="payment-form-wrapper">
          {error && <div className="error-message">{error}</div>}
          
          <form className="payment-form" onSubmit={handleSubmit}>
            <div className={`payment-form-content ${isRTL ? 'rtl' : 'ltr'}`}>
              <h2 className="form-section-title">
                {t('payment.enter-your-data') || 'يرجى إدخال بياناتك:'}
              </h2>
              
              <div className="form-field">
                <label htmlFor="name">
                  {t('payment.name') || 'الاسم'}:
                </label>
                <div className="input-wrapper">
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    onBlur={() => handleFieldBlur('name')}
                    className={`input-field ${fieldErrors.name ? 'error' : ''}`}
                    placeholder=""
                  />
                  {fieldErrors.name && (
                    <div className="field-error">{fieldErrors.name}</div>
                  )}
                </div>
              </div>
              
              <div className="form-field">
                <label htmlFor="phone">
                  {t('payment.phone') || 'رقم الهاتف'}:
                </label>
                <div className="input-wrapper">
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    onBlur={() => handleFieldBlur('phone')}
                    className={`input-field ${fieldErrors.phone ? 'error' : ''}`}
                    placeholder="9XXXXXXX أو 2XXXXXXX"
                    maxLength="8"
                  />
                  {fieldErrors.phone && (
                    <div className="field-error">{fieldErrors.phone}</div>
                  )}
                </div>
              </div>
              
              <div className="form-field">
                <label htmlFor="email">
                  {t('payment.email') || 'البريد الإلكتروني'}:
                </label>
                <div className="input-wrapper">
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    onBlur={() => handleFieldBlur('email')}
                    className={`input-field ${fieldErrors.email ? 'error' : ''}`}
                    placeholder=""
                  />
                  {fieldErrors.email && (
                    <div className="field-error">{fieldErrors.email}</div>
                  )}
                </div>
              </div>
              
              <div className="form-field">
                <label htmlFor="notes">
                  {t('payment.notes') || 'ملاحظات'}:
                </label>
                <div className="input-wrapper">
                  <input
                    type="text"
                    id="notes"
                    name="notes"
                    value={formData.notes}
                    onChange={handleInputChange}
                    className="input-field"
                    placeholder=""
                  />
                </div>
              </div>
            </div>
            
            <div className={`bank-transfer-section ${isRTL ? 'rtl' : 'ltr'}`}>
              <h3 className="bank-transfer-title">{t('payment.bank-transfer-title')}</h3>
              <div className="bank-details">
                <div className="bank-detail-row">
                  <span className="bank-detail-label">{t('payment.bank-name-label')}:</span>
                  <span className="bank-detail-value">{t('payment.bank-name')}</span>
                </div>
                <div className="bank-detail-row">
                  <span className="bank-detail-label">{t('payment.bank-account-label')}:</span>
                  <span className="bank-detail-value bank-account-number">{t('payment.bank-account')}</span>
                </div>
                <div className="bank-detail-row">
                  <span className="bank-detail-label">{t('payment.bank-beneficiary-label')}:</span>
                  <span className="bank-detail-value">{t('payment.bank-beneficiary')}</span>
                </div>
              </div>
            </div>

            <div className={`payment-actions ${isRTL ? 'rtl' : 'ltr'}`}>
              <button 
                type="button" 
                className="back-btn" 
                onClick={handleBack}
                disabled={isLoading}
              >
                {t('payment.back') || 'رجوع'}
              </button>
              <button 
                type="submit" 
                className="confirm-btn" 
                disabled={isLoading}
              >
                {t('payment.confirm-order') || 'تأكيد الطلب'}
              </button>
            </div>
          </form>
          
          {/* Loader Overlay */}
          {isLoading && (
            <div className="loader-overlay">
              <div className="loader-container">
                <svg width="143" height="143" viewBox="0 0 143 143" fill="none" xmlns="http://www.w3.org/2000/svg" className="loader-svg">
                  <circle cx="71.4808" cy="71.4806" r="44.5156" transform="rotate(150 71.4808 71.4806)" stroke="#17224D" strokeWidth="1.58984"/>
                  <path d="M32.9127 93.7479C29.4999 95.7182 28.2943 100.12 30.7157 103.229C34.8743 108.569 40.0563 113.058 45.9829 116.421C53.9862 120.962 63.0569 123.283 72.2575 123.145C81.4582 123.006 90.455 120.413 98.3181 115.634C104.141 112.095 109.186 107.452 113.182 101.99C115.509 98.8091 114.171 94.4452 110.701 92.5784C107.23 90.7115 102.951 92.069 100.46 95.1225C97.782 98.4045 94.5551 101.221 90.906 103.439C85.2146 106.899 78.7025 108.776 72.043 108.876C65.3834 108.976 58.818 107.296 53.0251 104.009C49.311 101.902 46.0009 99.183 43.2258 95.9829C40.644 93.0058 36.3254 91.7775 32.9127 93.7479Z" fill="#17224D"/>
                </svg>
                <p className="loader-text">
                  {t('payment.processing') || 'جاري المعالجة...'}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Payment;